import React, { useMemo, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
} from '@roger/ui-components';
import { CheckCircle, XCircle, MinusCircle, Undo2 } from 'lucide-react';
import type {
  ChecklistItemDetails,
  CodingExtractionItem,
} from '../../../types/codingData.types';

// Checklist Item Component with optional popover and click-to-toggle
export const ChecklistItem: React.FC<{
  label: string;
  status: 'complete' | 'missing' | 'not-applicable';
  details?: ChecklistItemDetails;
  variant?: 'default' | 'compact';
  /** When provided, the badge is clickable and toggles the manual override */
  onToggle?: () => void;
}> = ({ label, status, details, variant = 'default', onToggle }) => {
  const isCompact = variant === 'compact';
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Filter items based on showOnlySignedItems flag
  const displayItems = useMemo(() => {
    if (!details?.items) return [];

    if (details.showOnlySignedItems) {
      // For "Signed" fields, only show items where is_signed.value is true
      return details.items.filter(
        item => item.validations?.is_signed?.value === true
      );
    }

    // For "Attached" fields, show all items
    return details.items;
  }, [details?.items, details?.showOnlySignedItems]);

  // For "Signed" fields that are missing, show items where is_signed is not true
  const unsignedItems = useMemo(() => {
    if (!details?.items || !details.showOnlySignedItems) return [];
    return details.items.filter(
      item => item.validations?.is_signed?.value !== true
    );
  }, [details?.items, details?.showOnlySignedItems]);

  const getIcon = () => {
    switch (status) {
      case 'complete':
        return <CheckCircle className='h-4 w-4 text-green-600' />;
      case 'missing':
        return <XCircle className='h-4 w-4 text-red-600' />;
      case 'not-applicable':
        return <MinusCircle className='h-4 w-4 text-gray-400' />;
    }
  };

  const getTextColor = () => {
    switch (status) {
      case 'complete':
        return 'text-green-700';
      case 'missing':
        return 'text-red-700';
      case 'not-applicable':
        return 'text-gray-500';
    }
  };

  const getBgColor = () => {
    switch (status) {
      case 'complete':
        return 'bg-green-50 border-green-100';
      case 'missing':
        return 'bg-red-50 border-red-100';
      case 'not-applicable':
        return 'bg-white';
    }
  };

  // Render a single item card (used in popover content)
  const renderItemCard = (
    item: CodingExtractionItem,
    index: number,
    isSignedView: boolean,
    cardVariant: 'success' | 'error'
  ) => {
    const bgClass =
      cardVariant === 'success'
        ? 'bg-green-50 border-green-100'
        : 'bg-red-50 border-red-100';
    const borderClass =
      cardVariant === 'success' ? 'border-green-200' : 'border-red-200';

    return (
      <div key={index} className={`p-3 ${bgClass} border rounded-lg space-y-2`}>
        <div className='text-sm text-gray-800'>
          {item.face_to_face_visit_summary ||
            item.home_health_order ||
            item.authorized_diagnoses}
        </div>
        {item.source && Object.keys(item.source).length > 0 && (
          <div className='text-xs text-gray-500'>
            <span className='font-medium'>Source: </span>
            {Object.entries(item.source).map(([doc, pages], docIndex) => (
              <span key={doc}>
                {docIndex > 0 ? ', ' : ''}
                {doc} (pages {pages.join(', ')})
              </span>
            ))}
          </div>
        )}
        {isSignedView && item.validations?.is_signed?.reasoning ? (
          <div
            className={`text-xs text-gray-600 italic border-t ${borderClass} pt-2 mt-2`}
          >
            <span className='font-medium'>Signature: </span>
            {item.validations.is_signed.reasoning}
          </div>
        ) : (
          item.reasoning && (
            <div
              className={`text-xs text-gray-600 italic border-t ${borderClass} pt-2 mt-2`}
            >
              {item.reasoning}
            </div>
          )
        )}
      </div>
    );
  };

  // Determine if we have details to show in popover (extraction content or manual override message)
  const hasDetails =
    details &&
    (displayItems.length > 0 ||
      unsignedItems.length > 0 ||
      details.notQualifyingReasoning ||
      (details.manualOverrideMessage ?? '').length > 0);

  const badgeClassName = `flex items-center ${isCompact ? 'gap-1 px-1 py-1' : 'gap-2 px-3 py-2'} ${!isCompact && getBgColor()} ${!isCompact && 'border'} rounded-lg ${hasDetails && !onToggle ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''} ${onToggle ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`;

  const badgeContent = (
    <>
      {getIcon()}
      <span className={`text-xs font-medium ${getTextColor()}`}>{label}</span>
    </>
  );

  // Shared popover content (explanation + optional toggle button)
  const renderPopoverContent = (options?: { showToggleButton?: boolean }) => {
    const { showToggleButton = false } = options ?? {};
    return (
      <div className='space-y-3'>
        <div className='flex items-center justify-between gap-2'>
          <div className='flex items-center gap-2 min-w-0'>
            {getIcon()}
            <h4 className='font-semibold text-sm truncate'>{label}</h4>
          </div>
          {showToggleButton && onToggle && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='shrink-0'
              onClick={() => {
                onToggle();
                setPopoverOpen(false);
              }}
              aria-label={
                status === 'complete'
                  ? `Mark ${label} as not present`
                  : `Mark ${label} as present`
              }
            >
              {status === 'complete' ? (
                <>
                  <Undo2 className='h-4 w-4' />
                  Mark as not present
                </>
              ) : (
                <>
                  <CheckCircle className='h-4 w-4' />
                  Mark as present
                </>
              )}
            </Button>
          )}
        </div>
        {details?.manualOverrideMessage ? (
          <div className='p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700'>
            {details.manualOverrideMessage}
          </div>
        ) : (
          <>
            {status === 'complete' && displayItems.length > 0 && (
              <div className='space-y-3'>
                {displayItems.map((item, index) =>
                  renderItemCard(
                    item,
                    index,
                    details!.showOnlySignedItems ?? false,
                    'success'
                  )
                )}
              </div>
            )}
            {status === 'missing' &&
              details?.showOnlySignedItems &&
              unsignedItems.length > 0 && (
                <div className='space-y-3'>
                  <div className='text-xs text-red-700 font-medium'>
                    Documents found but not properly signed (see reasoning
                    below):
                  </div>
                  {unsignedItems.map((item, index) =>
                    renderItemCard(item, index, true, 'error')
                  )}
                </div>
              )}
            {status === 'missing' &&
              details?.notQualifyingReasoning &&
              (!details?.showOnlySignedItems ||
                (details?.showOnlySignedItems &&
                  unsignedItems.length === 0)) && (
                <div className='p-3 bg-red-50 border border-red-100 rounded-lg'>
                  <div className='text-sm text-gray-700'>
                    <span className='font-medium text-red-700'>
                      Why this is missing:{' '}
                    </span>
                    {details.notQualifyingReasoning}
                  </div>
                </div>
              )}
            {displayItems.length === 0 &&
              unsignedItems.length === 0 &&
              !details?.notQualifyingReasoning && (
                <div className='text-sm text-gray-500 italic'>
                  No additional details available.
                </div>
              )}
          </>
        )}
      </div>
    );
  };

  const itemContent =
    onToggle != null ? (
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type='button'
            className={`${badgeClassName} border-0 text-left`}
            aria-label={`View details for ${label}`}
          >
            {badgeContent}
          </button>
        </PopoverTrigger>
        <PopoverContent className='w-96 max-h-80 overflow-y-auto' align='start'>
          {renderPopoverContent({ showToggleButton: true })}
        </PopoverContent>
      </Popover>
    ) : (
      <div className={badgeClassName}>{badgeContent}</div>
    );

  if (!hasDetails && !onToggle) {
    return itemContent;
  }

  // When onToggle is set we already rendered badge + popover above
  if (onToggle != null) {
    return itemContent;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{itemContent}</PopoverTrigger>
      <PopoverContent className='w-96 max-h-80 overflow-y-auto' align='start'>
        {renderPopoverContent()}
      </PopoverContent>
    </Popover>
  );
};
